import type { CartItemDTO, ProductOfferingDTO } from '@t/api.types';
import type { SelectedAddOn, SelectedEquipment } from '@t/configuration.types';
import type { PlanAddOn } from '@t/productOffering.types';

type AddOnChildInput = Required<Pick<PlanAddOn, 'id' | 'name' | 'quantity'>>;

export type CartMutations = {
  selectAddOnsAsync: (offeringCode: string, items: AddOnChildInput[]) => Promise<unknown>;
  removeAddOnAsync: (id: string) => Promise<unknown>;
  removeAddOnsAsync: (ids: string[]) => Promise<unknown>;
};

export interface SaveEquipmentParams {
  parentItem: CartItemDTO | undefined;
  originalEquipment: SelectedEquipment | null;
  selectedEquipment: SelectedEquipment | null;
  offeringCode: string;
  equipment: ProductOfferingDTO[];
  mutations: CartMutations;
}

export interface SaveAddOnsParams {
  parentItem: CartItemDTO | undefined;
  offeringCode: string;
  originalAddOns: SelectedAddOn[];
  currentAddOns: SelectedAddOn[];
  planAddOns: PlanAddOn[] | undefined;
  mutations: CartMutations;
}

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/** Find child cart items matching a product offering ID under a parent. */
const findChildrenByOffering = (parent: CartItemDTO | undefined, offeringId: string): CartItemDTO[] =>
  parent?.childItems?.filter((c) => c.productOffering?.id === offeringId) ?? [];

// ---------------------------------------------------------------------------
// Strategy: Equipment (Single Responsibility)
// ---------------------------------------------------------------------------

export const saveEquipmentChanges = async ({
  parentItem,
  originalEquipment,
  selectedEquipment,
  offeringCode,
  equipment,
  mutations,
}: SaveEquipmentParams): Promise<void> => {
  const oldChildId = findChildrenByOffering(parentItem, originalEquipment?.id ?? '')[0]?.id;
  if (oldChildId) {
    await mutations.removeAddOnAsync(oldChildId);
  }

  if (!selectedEquipment) return;

  const dto = equipment.find((e) => e.id === selectedEquipment.id);

  if (dto) {
    await mutations.selectAddOnsAsync(offeringCode, [
      {
        id: dto.id ?? selectedEquipment.id,
        name: dto.name ?? 'Equipment',
        quantity: selectedEquipment.quantity,
      },
    ]);
  }
};

// ---------------------------------------------------------------------------
// Strategy: Add-ons (Single Responsibility)
// ---------------------------------------------------------------------------

export const saveAddOnChanges = async ({
  parentItem,
  offeringCode,
  originalAddOns,
  currentAddOns,
  planAddOns,
  mutations,
}: SaveAddOnsParams): Promise<void> => {
  const originalCodes = new Map(originalAddOns.map((a) => [a.code, a.quantity]));
  const currentCodes = new Map(currentAddOns.map((a) => [a.code, a.quantity]));
  const codeToId = new Map(planAddOns?.map((a) => [a.code, a.id]));

  const getChildIds = (code: string) =>
    findChildrenByOffering(parentItem, codeToId.get(code) ?? code).flatMap((c) => (c.id ? [c.id] : []));

  const findAddOn = (code: string) => planAddOns?.find((a) => a.code === code);

  const changes = [...currentCodes]
    .map(([code, qty]) => ({ code, diff: qty - (originalCodes.get(code) ?? qty) }))
    .filter(({ diff }) => diff !== 0);

  const removedIds = [...originalCodes.keys()].filter((code) => !currentCodes.has(code)).flatMap(getChildIds);
  if (removedIds.length > 0) {
    await mutations.removeAddOnsAsync(removedIds);
  }

  // Edit flow still uses "one cart row per unit" because quantity decrements rely on
  // removing individual child cart-item ids. Each unit is a separate {quantity: 1} row.
  const itemsToAdd: AddOnChildInput[] = [
    ...[...currentCodes]
      .filter(([code]) => !originalCodes.has(code))
      .flatMap(([code, quantity]) => {
        const addOn = findAddOn(code);
        return addOn ? Array.from({ length: quantity }, () => ({ id: addOn.id, name: addOn.name, quantity: 1 })) : [];
      }),
    ...changes
      .filter(({ diff }) => diff > 0)
      .flatMap(({ code, diff }) => {
        const addOn = findAddOn(code);
        return addOn ? Array.from({ length: diff }, () => ({ id: addOn.id, name: addOn.name, quantity: 1 })) : [];
      }),
  ];

  if (itemsToAdd.length > 0) {
    await mutations.selectAddOnsAsync(offeringCode, itemsToAdd);
  }

  const decreasedIds = changes
    .filter(({ diff }) => diff < 0)
    .flatMap(({ code, diff }) => getChildIds(code).slice(0, Math.abs(diff)));
  if (decreasedIds.length > 0) {
    await mutations.removeAddOnsAsync(decreasedIds);
  }
};
